import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Coins, CreditCard, Package, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useCredits } from '../../contexts/CreditsContext';
import { useAuth } from '../../contexts/AuthContext';
import ShareCredits from './ShareCredits';
import TransactionHistory from './TransactionHistory';

const PAYSTACK_PUBLIC_KEY = "pk_live_bfa55cb104a80b9a742b78913aa7e3ed8d7b943b";

const CreditsPage = () => {
  const { credits, totalPurchased, totalUsed, loading, error, fetchUserCredits, initializePayment, verifyPayment } = useCredits();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(null);

  const [exchangeRate, setExchangeRate] = useState(null);
  const [isLoadingRate, setIsLoadingRate] = useState(false);

  useEffect(() => {
    fetchExchangeRate();
  }, []);
  
  // Function to fetch exchange rate
  const fetchExchangeRate = async () => {
    try {
      setIsLoadingRate(true);
      // You can use free APIs like ExchangeRate-API, Open Exchange Rates, or Fixer.io
      // You may need to register for an API key depending on the service
      const response = await fetch('https://open.er-api.com/v6/latest/USD');
      const data = await response.json();
      
      if (data && data.rates && data.rates.ZAR) {
        setExchangeRate(data.rates.ZAR);
      } else {
        // Fallback to a reasonable default if API fails
        setExchangeRate(18.5); // Example rate, update as needed
        console.warn('Using fallback exchange rate');
      }
    } catch (error) {
      console.error('Failed to fetch exchange rate:', error);
      // Fallback to a reasonable default
      setExchangeRate(18.5); // Example rate, update as needed
    } finally {
      setIsLoadingRate(false);
    }
  };
  
  
  // Check for payment reference in URL (after redirect from Paystack)
  useEffect(() => {
    // Initial fetch of credits data
    fetchUserCredits();
    
    // Check for payment references in URL
    const queryParams = new URLSearchParams(location.search);
    const reference = queryParams.get('reference');
    
    if (reference) {
      verifyPaymentReference(reference);
    }
  }, [location.search]); // Only depend on the search params changing
  
  // Handle Paystack redirect verification
  const verifyPaymentReference = async (reference) => {
    try {
      setProcessingPayment(true);
      setPaymentError(null);
      
      const result = await verifyPayment(reference);
      
      if (result.status === true || result.status === 'success') {
        setPaymentSuccess(`Payment successful! Your credits have been added to your account.`);
        // Remove reference from URL
        navigate('/credits', { replace: true });
        
        // Important: Refresh the credits display
        await fetchUserCredits();
      } else {
        setPaymentError('Payment verification failed. Please contact support if your credits were not added.');
      }
    } catch (error) {
      setPaymentError(error.message || 'Payment verification failed');
    } finally {
      setProcessingPayment(false);
    }
  };
  
  // Credit package options
  const creditPackages = [
    { id: '5', name: '5 Credits', price: 500, credits: 5 },
    { id: '15', name: '15 Credits', price: 1500, credits: 15 },
    { id: '25', name: '25 Credits', price: 2500, credits: 25 }
  ];
  
  // Handle package selection
  const handlePackageSelect = (packageId) => {
    setSelectedPackage(packageId);
    setCustomAmount('');
  };
  
  // Handle custom amount change
  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setCustomAmount(value);
      setSelectedPackage('flexi');
    }
  };
  
  // Initialize payment
  const handlePayment = async () => {
    try {
      setProcessingPayment(true);
      setPaymentError(null);
      
      let amountUSD;
      let packageType;
      
      if (selectedPackage === 'flexi') {
        // Convert to cents (e.g., $5 = 500 cents)
        amountUSD = parseInt(customAmount) * 100;
        packageType = 'flexi';
        
        if (amountUSD < 500) {
          throw new Error('Minimum amount for Flexi package is USD 5');
        }
      } else {
        const selectedPkg = creditPackages.find(pkg => pkg.id === selectedPackage);
        if (!selectedPkg) {
          throw new Error('Please select a credit package');
        }
        amountUSD = selectedPkg.price;
        packageType = selectedPkg.id;
      }
      
      // Convert USD to ZAR using the exchange rate
      // Paystack expects amount in the smallest currency unit (kobo for NGN, cents for ZAR)
      const amountZAR = Math.round(amountUSD * (exchangeRate || 18.5));
      
      const result = await initializePayment(amountZAR, packageType, amountUSD);
      
      if (result.status) {
        // Redirect to Paystack payment page
        window.location.href = result.data.authorization_url;
      } else {
        throw new Error(result.message || 'Payment initialization failed');
      }
    } catch (error) {
      setPaymentError(error.message);
    } finally {
      setProcessingPayment(false);
    }
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return `USD ${(amount / 100).toFixed(2)}`;
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading credits information...</span>
      </div>
    );
  }
  
  return (
    <div className="h-full">
      <h1 className="text-3xl font-bold text-blue-800 mb-6">
        Credits
      </h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}
      
      {paymentError && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-start">
          <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>{paymentError}</div>
        </div>
      )}
      
      {paymentSuccess && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 flex items-start">
          <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>{paymentSuccess}</div>
        </div>
      )}
      
      {/* Credit Summary */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Your Credit Balance</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex items-center">
            <div className="bg-blue-500 p-3 rounded-full mr-4">
              <Coins className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Available Credits</div>
              <div className="text-2xl font-bold">{credits}</div>
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-100 flex items-center">
            <div className="bg-green-500 p-3 rounded-full mr-4">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Total Purchased</div>
              <div className="text-2xl font-bold">{totalPurchased}</div>
            </div>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-100 flex items-center">
            <div className="bg-purple-500 p-3 rounded-full mr-4">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-sm text-gray-500">Total Used</div>
              <div className="text-2xl font-bold">{totalUsed}</div>
            </div>
          </div>
        </div>
        
        <div className="mt-4 text-sm text-gray-600">
          <p>Each analysis costs 5 credits. Purchase more credits below to continue analyzing your performances.</p>
        </div>
      </div>
      
      {/* Purchase Credits */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Purchase Credits</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {creditPackages.map(pkg => (
            <div 
              key={pkg.id}
              onClick={() => handlePackageSelect(pkg.id)}
              className={`p-4 rounded-lg border cursor-pointer transition-all ${
                selectedPackage === pkg.id 
                  ? 'border-blue-500 bg-blue-50 shadow-md' 
                  : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="font-semibold">{pkg.name}</div>
                <div className="text-green-600 font-bold">{formatCurrency(pkg.price)}</div>
              </div>
              <div className="text-sm text-gray-600">
                Use for {pkg.credits} analyses
              </div>
              {exchangeRate && (
                <div className="text-xs text-gray-500 mt-1">
                  Approx. ZAR {((pkg.price/100) * exchangeRate).toFixed(2)}
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Flexi Option */}
        <div
          onClick={() => setSelectedPackage('flexi')}
          className={`p-4 rounded-lg border mb-6 cursor-pointer transition-all ${
            selectedPackage === 'flexi' && customAmount && exchangeRate && (
              <div className="mt-2 text-sm text-gray-600">
                You'll receive {customAmount} credits for {formatCurrency(customAmount * 100)}
                <div className="text-xs text-gray-500">
                  Approx. ZAR {(customAmount * exchangeRate).toFixed(2)}
                </div>
              </div>
            )
          }`}
        >
          <div className="flex justify-between items-center mb-2">
            <div className="font-semibold">Flexi Credits</div>
            <div className="text-green-600 font-bold">Custom Amount</div>
          </div>
          <div className="text-sm text-gray-600 mb-2">
            Choose your own amount (minimum USD 5)
          </div>
          <div className="flex items-center mt-2">
            <span className="text-gray-500 mr-2">USD</span>
            <input
              type="text"
              value={customAmount}
              onChange={handleCustomAmountChange}
              placeholder="Enter amount (e.g. 10)"
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {selectedPackage === 'flexi' && customAmount && (
            <div className="mt-2 text-sm text-gray-600">
              You'll receive {customAmount} credits for {formatCurrency(customAmount * 100)}
            </div>
          )}
        </div>
        
        <div className="flex justify-center">
          <button
            onClick={handlePayment}
            disabled={processingPayment || !selectedPackage || (selectedPackage === 'flexi' && (!customAmount || parseInt(customAmount) < 5))}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {processingPayment ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-4 w-4" />
                Proceed to Payment
              </>
            )}
          </button>
        </div>
        
        <div className="mt-6 text-sm text-gray-500">
          <p className="mb-2">Notes:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Payments are processed securely via Paystack.</li>
            <li className="text-red-500">Credits MAY not roll over to the new month.</li>
            <li>For payment issues, please contact support.</li>
          </ul>
        </div>
      </div>
      
      {/* Credit Sharing Section */}
      {credits > 0 && (
        <div className="mt-8">
          <ShareCredits />
        </div>
      )}

      {/* Transaction History Section */}
        <div className="mt-8">
          <TransactionHistory />
        </div>
    </div>
  );
};

export default CreditsPage;